import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { SearchBar } from '../components/SearchBar';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { TableFilter } from '../components/TableFilter';
import type { Filter } from '../components/TableFilter';
import { RefreshCw, Package, Plus, MoreVertical, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
// Mock product data
const mockProducts = [{
  id: '1',
  name: '2022 Reserve Shiraz',
  description: 'Full-bodied red wine with rich berry flavors',
  imageUrl: '',
  rrp: 48.0,
  salePrice: 45.0,
  stock: 156,
  stockStatus: 'In Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '15/01/2024',
  tags: ['Red Wine', 'Premium']
}, {
  id: '2',
  name: '2021 Chardonnay Reserve',
  description: 'Elegant white wine with citrus notes',
  imageUrl: '',
  rrp: 42.0,
  salePrice: 38.0,
  stock: 8,
  stockStatus: 'Low Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '12/01/2024',
  tags: ['White Wine', 'Premium']
}, {
  id: '3',
  name: 'Pinot Noir Estate 2020',
  description: 'Smooth and refined with cherry undertones',
  imageUrl: '',
  rrp: 55.0,
  salePrice: 52.0,
  stock: 0,
  stockStatus: 'Out of Stock' as const,
  syncStatus: 'SYNC_ERROR' as const,
  createdAt: '08/01/2024',
  tags: ['Red Wine']
}, {
  id: '4',
  name: 'Sauvignon Blanc 2023',
  description: 'Crisp and refreshing with tropical fruit',
  imageUrl: '',
  rrp: 32.0,
  salePrice: 28.0,
  stock: 243,
  stockStatus: 'In Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '20/12/2023',
  tags: ['White Wine']
}, {
  id: '5',
  name: 'Cabernet Sauvignon 2019',
  description: 'Bold and complex with dark fruit flavors',
  imageUrl: '',
  rrp: 58.0,
  salePrice: 55.0,
  stock: 89,
  stockStatus: 'In Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '15/12/2023',
  tags: ['Red Wine', 'Premium']
}, {
  id: '6',
  name: 'Rosé Grenache 2023',
  description: 'Light and fruity summer wine',
  imageUrl: '',
  rrp: 28.0,
  salePrice: 25.0,
  stock: 6,
  stockStatus: 'Low Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '10/12/2023',
  tags: ['Rosé']
}, {
  id: '7',
  name: 'Merlot Reserve 2020',
  description: 'Smooth and velvety with plum notes',
  imageUrl: '',
  rrp: 46.0,
  salePrice: 42.0,
  stock: 124,
  stockStatus: 'In Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '05/12/2023',
  tags: ['Red Wine']
}, {
  id: '8',
  name: 'Riesling 2023',
  description: 'Sweet and aromatic with floral notes',
  imageUrl: '',
  rrp: 35.0,
  salePrice: 32.0,
  stock: 0,
  stockStatus: 'Out of Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '01/12/2023',
  tags: ['White Wine']
}, {
  id: '9',
  name: 'Tempranillo 2021',
  description: 'Spanish-style red with spicy finish',
  imageUrl: '',
  rrp: 44.0,
  salePrice: 40.0,
  stock: 67,
  stockStatus: 'In Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '28/11/2023',
  tags: ['Red Wine']
}, {
  id: '10',
  name: 'Viognier 2023',
  description: 'Aromatic white with stone fruit flavors',
  imageUrl: '',
  rrp: 38.0,
  salePrice: 35.0,
  stock: 4,
  stockStatus: 'Low Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '25/11/2023',
  tags: ['White Wine', 'Premium']
}];
// Top selling products data
const topSellingProducts = [{
  name: '2022 Reserve Shiraz',
  sold: 342
}, {
  name: '2021 Chardonnay Reserve',
  sold: 287
}, {
  name: 'Sauvignon Blanc 2023',
  sold: 256
}];
export function ProductsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage_, setCurrentPage_] = useState(1);
  const pageSize = 10;
  // Filter state
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({
    stockStatus: [],
    tags: [],
    created: []
  });
  const [addedFilters, setAddedFilters] = useState<Set<string>>(new Set());
  const [openFilterDropdowns, setOpenFilterDropdowns] = useState<Set<string>>(new Set());
  const [customDateRanges, setCustomDateRanges] = useState<Record<string, {
    startDate: string;
    endDate: string;
  }>>({});
  // State for managing which product's dropdown is open
  const [openProductDropdown, setOpenProductDropdown] = useState<string | null>(null);
  // Sort state
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  // Calculate stats
  const stats = useMemo(() => {
    const lowStock = mockProducts.filter(p => p.stockStatus === 'Low Stock').length;
    const outOfStock = mockProducts.filter(p => p.stockStatus === 'Out of Stock').length;
    const totalProducts = mockProducts.length;
    return {
      lowStock,
      outOfStock,
      totalProducts
    };
  }, []);
  // Available filters
  const availableFilters: Filter[] = [{
    id: 'stockStatus',
    label: 'Stock Status',
    options: [{
      id: '1',
      label: 'In Stock',
      value: 'in-stock'
    }, {
      id: '2',
      label: 'Out of Stock',
      value: 'out-of-stock'
    }, {
      id: '3',
      label: 'Low Stock',
      value: 'low-stock'
    }],
    selectedValues: filterValues.stockStatus || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('stockStatus')
  }, {
    id: 'tags',
    label: 'Tags',
    options: [{
      id: '1',
      label: 'Red Wine',
      value: 'red-wine'
    }, {
      id: '2',
      label: 'White Wine',
      value: 'white-wine'
    }, {
      id: '3',
      label: 'Rosé',
      value: 'rose'
    }, {
      id: '4',
      label: 'Premium',
      value: 'premium'
    }],
    selectedValues: filterValues.tags || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('tags')
  }, {
    id: 'created',
    label: 'Created',
    options: [{
      id: '1',
      label: 'Last 7 days',
      value: 'last-7-days'
    }, {
      id: '2',
      label: 'Last 30 days',
      value: 'last-30-days'
    }, {
      id: '3',
      label: 'Last 90 days',
      value: 'last-90-days'
    }, {
      id: '4',
      label: 'Custom',
      value: 'custom'
    }],
    selectedValues: filterValues.created || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('created'),
    customDateRange: customDateRanges['created']
  }];
  // Filter products based on search and filters
  const filteredProducts = useMemo(() => {
    let products = mockProducts;
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      products = products.filter(product => product.name.toLowerCase().includes(query) || product.description.toLowerCase().includes(query));
    }
    // Stock status filter
    if (filterValues.stockStatus && filterValues.stockStatus.length > 0) {
      products = products.filter(product => {
        return filterValues.stockStatus.some(value => {
          if (value === 'in-stock') return product.stockStatus === 'In Stock';
          if (value === 'out-of-stock') return product.stockStatus === 'Out of Stock';
          if (value === 'low-stock') return product.stockStatus === 'Low Stock';
          return false;
        });
      });
    }
    // Tags filter
    if (filterValues.tags && filterValues.tags.length > 0) {
      products = products.filter(product => {
        return filterValues.tags.some(value => {
          const tagMap: Record<string, string> = {
            'red-wine': 'Red Wine',
            'white-wine': 'White Wine',
            rose: 'Rosé',
            premium: 'Premium'
          };
          return product.tags?.includes(tagMap[value]);
        });
      });
    }
    // Created date filter
    if (filterValues.created && filterValues.created.length > 0) {
      products = products.filter(product => {
        const createdDate = new Date(product.createdAt.split('/').reverse().join('-'));
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
        return filterValues.created.some(value => {
          if (value === 'last-7-days') return daysDiff <= 7;
          if (value === 'last-30-days') return daysDiff <= 30;
          if (value === 'last-90-days') return daysDiff <= 90;
          if (value === 'custom') {
            const dateRange = customDateRanges['created'];
            if (dateRange && dateRange.startDate && dateRange.endDate) {
              const start = new Date(dateRange.startDate);
              const end = new Date(dateRange.endDate);
              end.setHours(23, 59, 59, 999);
              return createdDate >= start && createdDate <= end;
            }
            return true;
          }
          return false;
        });
      });
    }
    // Apply sorting
    if (sortConfig) {
      products = [...products].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        switch (sortConfig.key) {
          case 'product':
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
            break;
          case 'rrp':
            aValue = a.rrp;
            bValue = b.rrp;
            break;
          case 'salePrice':
            aValue = a.salePrice;
            bValue = b.salePrice;
            break;
          case 'stock':
            aValue = a.stock;
            bValue = b.stock;
            break;
          case 'stockStatus':
            const statusOrder: Record<string, number> = {
              'Out of Stock': 0,
              'Low Stock': 1,
              'In Stock': 2
            };
            aValue = statusOrder[a.stockStatus] ?? 0;
            bValue = statusOrder[b.stockStatus] ?? 0;
            break;
          case 'created':
            aValue = new Date(a.createdAt.split('/').reverse().join('-')).getTime();
            bValue = new Date(b.createdAt.split('/').reverse().join('-')).getTime();
            break;
          default:
            return 0;
        }
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return products;
  }, [searchQuery, filterValues, customDateRanges, sortConfig]);
  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / pageSize);
  const paginatedProducts = filteredProducts.slice((currentPage_ - 1) * pageSize, currentPage_ * pageSize);
  // Check if any filters are active
  const hasActiveFilters = addedFilters.size > 0 || searchQuery.trim() !== '';
  // Get stock status badge variant
  const getStockStatusVariant = (status: string): 'success' | 'warning' | 'error' => {
    if (status === 'In Stock') return 'success';
    if (status === 'Low Stock') return 'warning';
    return 'error';
  };
  // Get sync status badge variant
  const getSyncStatusVariant = (status: string): 'success' | 'error' => {
    return status === 'IN_SYNC' ? 'success' : 'error';
  };
  // Handle filter change
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    const newFilterValues = {
      ...filterValues,
      [filterId]: Array.isArray(value) ? value : [value]
    };
    setFilterValues(newFilterValues);
  };
  // Handle filter remove
  const handleFilterRemove = (filterId: string) => {
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.delete(filterId);
    setAddedFilters(newAddedFilters);
    // Clear filter values
    const newFilterValues = {
      ...filterValues
    };
    newFilterValues[filterId] = [];
    setFilterValues(newFilterValues);
  };
  // Handle add filter
  const handleAddFilter = (filterId: string) => {
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.add(filterId);
    setAddedFilters(newAddedFilters);
  };
  // Handle toggle filter dropdown
  const handleToggleFilterDropdown = (filterId: string) => {
    const newOpenFilterDropdowns = new Set(openFilterDropdowns);
    newOpenFilterDropdowns.add(filterId);
    setOpenFilterDropdowns(newOpenFilterDropdowns);
  };
  // Handle custom date change
  const handleCustomDateChange = (filterId: string, startDate: string, endDate: string) => {
    setCustomDateRanges(prev => ({
      ...prev,
      [filterId]: {
        startDate,
        endDate
      }
    }));
  };
  // Handle clear all filters
  const handleClearAll = () => {
    setFilterValues({
      stockStatus: [],
      tags: [],
      created: []
    });
    setAddedFilters(new Set());
    setSearchQuery('');
  };
  // Handle sort
  const handleSort = (columnKey: string) => {
    setSortConfig(current => {
      if (!current || current.key !== columnKey) {
        return {
          key: columnKey,
          direction: 'asc'
        };
      }
      if (current.direction === 'asc') {
        return {
          key: columnKey,
          direction: 'desc'
        };
      }
      return null;
    });
  };
  // Handle low stock card click
  const handleLowStockClick = () => {
    setFilterValues({
      stockStatus: ['low-stock'],
      tags: [],
      created: []
    });
    setAddedFilters(new Set(['stockStatus']));
  };
  // Handle out of stock card click
  const handleOutOfStockClick = () => {
    setFilterValues({
      stockStatus: ['out-of-stock'],
      tags: [],
      created: []
    });
    setAddedFilters(new Set(['stockStatus']));
  };
  // Handle view product in Shopify
  const handleViewProductInShopify = (productId: string) => {
    // In a real app, this would open the Shopify admin URL
    console.log(`View product ${productId} in Shopify`);
    window.open(`https://store.myshopify.com/admin/products/${productId}`, '_blank');
    setOpenProductDropdown(null);
  };
  return <div className="bg-[#F1F5F9] min-h-full p-4 md:p-6" data-id="element-12631" data-mp="2olj0l1v9szt7">
      {/* NavBar */}
      <div className="mb-4 md:mb-6" data-id="element-12632" data-mp="2ojg6o6qzymj7">
        <NavBar tabs={[]} title="Products" data-id="element-12633" data-mp="2kkh6ezg7ndy3" />
      </div>

      {/* Header Section */}
      <div className="mb-4 flex flex-col md:flex-row md:items-center justify-between gap-3" data-id="element-12634" data-mp="2yb2io5b5spn0">
        <p className="text-[13px] md:text-[14px] text-[#64748B]" data-id="element-12635" data-mp="2tkm1ca47wjvd">
          Products synced from your Shopify store
        </p>
      </div>

      {/* Summary Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8" data-id="element-12636" data-mp="38bmo7k5pogb1">
        {/* Top Selling Card */}
        <div className="bg-white rounded-lg border border-[#E2E8F0] p-4 md:p-5 shadow-0_1px_3px_rgba(15,23,42,0.08)" data-id="element-12637" data-mp="3h9y9r7rq0nwm">
          <div className="text-[13px] md:text-[14px] text-[#64748B] font-medium mb-3" data-id="element-12638" data-mp="3cavh6msxga5j">
            Top Selling
          </div>
          <div className="space-y-2" data-id="element-12639" data-mp="1mwp8225f26ox">
            {topSellingProducts.map((product, index) => <div key={index} className="flex items-center justify-between" data-id="element-12640" data-mp="1hy5izsvj0esc" data-mpi={index}>
                <span className="text-[13px] md:text-[14px] text-[#0F172A]" data-id="element-12641" data-mp="3tjxs0o7v37l4">
                  {product.name}
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-xl text-[11px] md:text-[12px] font-medium bg-[#DCFCE7] text-[#166534]" data-id="element-12642" data-mp="2dmv949vwszl">
                  {product.sold} sold
                </span>
              </div>)}
          </div>
        </div>

        {/* Low Stock Card */}
        <button onClick={handleLowStockClick} className="bg-white rounded-lg border border-[#E2E8F0] p-4 md:p-5 shadow-[0_1px_3px_rgba(15,23,42,0.08)] hover:shadow-[0_4px_6px_rgba(15,23,42,0.1)] transition-shadow cursor-pointer text-left w-full" data-id="element-12643" data-mp="3at6k7r3ivp8u">
          <div className="text-[13px] md:text-[14px] text-[#64748B] font-medium mb-3" data-id="element-12644" data-mp="35uvarhh53gr5">
            Low Stock
          </div>
          <div className="text-[28px] md:text-[32px] font-semibold text-[#F59E0B]" data-id="element-12645" data-mp="101dc7toq4c8g">
            {stats.lowStock}
          </div>
        </button>

        {/* Out of Stock Card */}
        <button onClick={handleOutOfStockClick} className="bg-white rounded-lg border border-[#E2E8F0] p-4 md:p-5 shadow-[0_1px_3px_rgba(15,23,42,0.08)] hover:shadow-[0_4px_6px_rgba(15,23,42,0.1)] transition-shadow cursor-pointer text-left w-full" data-id="element-12646" data-mp="2d0f704jk2qli">
          <div className="text-[13px] md:text-[14px] text-[#64748B] font-medium mb-3" data-id="element-12647" data-mp="240aet1mxnyrx">
            Out of Stock
          </div>
          <div className="text-[28px] md:text-[32px] font-semibold text-[#EF4444]" data-id="element-12648" data-mp="1z2qmixtehlgz">
            {stats.outOfStock}
          </div>
        </button>

        {/* Total Products Card */}
        <div className="bg-white rounded-lg border border-[#E2E8F0] p-4 md:p-5 shadow-0_1px_3px_rgba(15,23,42,0.08)" data-id="element-12649" data-mp="2rq4oxbfvb98h">
          <div className="text-[13px] md:text-[14px] text-[#64748B] font-medium mb-3" data-id="element-12650" data-mp="2mw9yzq9o5y0y">
            Total Products
          </div>
          <div className="text-[28px] md:text-[32px] font-semibold text-[#0F172A]" data-id="element-12651" data-mp="3fffk8deg8bf8">
            {stats.totalProducts}
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg mb-4 md:mb-6 p-4 md:p-6 shadow-0_1px_3px_rgba(15,23,42,0.08)" data-id="element-12652" data-mp="3hzve68o3y91h">
        <div className="flex flex-col gap-4" data-id="element-12653" data-mp="3mz7yomrib40w">
          {/* Search + Add Filter Button Row */}
          <div className="flex items-center gap-3" data-id="element-12654" data-mp="3rwgn353904al">
            <div className="flex-1" data-id="element-12655" data-mp="3vz4st3wo0v3c">
              <SearchBar placeholder="Search products..." value={searchQuery} onChange={setSearchQuery} className="w-full" data-id="element-12656" data-mp="3w0651bpf3nmn" />
            </div>
            <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} onCustomDateChange={handleCustomDateChange} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyButton={true} data-id="element-12657" data-mp="4qpkcfujsd6z" />
          </div>

          {/* Active Filter Chips Row */}
          {addedFilters.size > 0 && <div className="flex items-center gap-3" data-id="element-12658" data-mp="nl4loc4c0hxz">
              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} onCustomDateChange={handleCustomDateChange} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyChips={true} data-id="element-12659" data-mp="shv1cyc85biv" />
              <button onClick={handleClearAll} className="text-[13px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-12660" data-mp="1ns27j4j5wibz">
                Clear all
              </button>
            </div>}
        </div>
      </div>

      {/* Products Table */}
      {filteredProducts.length > 0 ? <>
          <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden mb-4 md:mb-6 shadow-0_1px_3px_rgba(15,23,42,0.08)" data-id="element-12661" data-mp="q4jqz7j9ebxf">
            <div className="overflow-x-auto" data-id="element-12662" data-mp="14tzfegvbi59y">
              <table className="w-full" data-id="element-12663" data-mp="1027vuqakwztb">
                <thead className="bg-[#F1F5F9]" data-id="element-12664" data-mp="100dcvhivbhrv">
                  <tr data-id="element-12665" data-mp="cc3uyqorze60">
                    <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-12666" data-mp="cbkc97rawlqx">
                      <button onClick={() => handleSort('product')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-12667" data-mp="7cr7q4tzs52w">
                        Product
                        {sortConfig?.key === 'product' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-12668" data-mp="292q3tfop52dd" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-12669" data-mp="29416tdabi63f" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-12670" data-mp="2e19o7bnn663m" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-[100px]" data-id="element-12671" data-mp="31sf75zovpei5">
                      <button onClick={() => handleSort('rrp')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-12672" data-mp="36nuiajvi4wav">
                        RRP
                        {sortConfig?.key === 'rrp' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-12673" data-mp="1har20covj5ct" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-12674" data-mp="1h9z4uxfmozkf" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-12675" data-mp="1ld466wnz2v7h" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-[100px]" data-id="element-12676" data-mp="nmnadkhdx5wz">
                      <button onClick={() => handleSort('salePrice')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-12677" data-mp="dny2mkj7d1ug">
                        Sale Price
                        {sortConfig?.key === 'salePrice' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-12678" data-mp="3xcxee90wz2k" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-12679" data-mp="8ov0p8yirbqp" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-12680" data-mp="8qfswrxd2ile" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider w-[80px]" data-id="element-12681" data-mp="3r1170al9ygx8">
                      <button onClick={() => handleSort('stock')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors justify-end" data-id="element-12682" data-mp="3h4enpq8ljxxb">
                        Stock
                        {sortConfig?.key === 'stock' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-12683" data-mp="32k26y3rxfz51" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-12684" data-mp="2yh5pige4i92o" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-12685" data-mp="2yiqpilj7ik7x" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-[120px]" data-id="element-12686" data-mp="37gj1bfppz9qk">
                      <button onClick={() => handleSort('stockStatus')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-12687" data-mp="3lfaft38huxf8">
                        Stock Status
                        {sortConfig?.key === 'stockStatus' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-12688" data-mp="3q71xeo9o9st8" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-12689" data-mp="3q8bv3ccm4yzc" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-12690" data-mp="8ynbdv0q075a" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-[150px]" data-id="element-12691" data-mp="41wwhb0njhlg">
                      Tags
                    </th>
                    <th className="px-3 md:px-4 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider w-[120px]" data-id="element-12692" data-mp="g3ep4bm0qack">
                      <button onClick={() => handleSort('created')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors justify-end" data-id="element-12693" data-mp="kzwosx2hrii1">
                        Created
                        {sortConfig?.key === 'created' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-12694" data-mp="yrtder4yba9o" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-12695" data-mp="13s7n0dm7as9t" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-12696" data-mp="13squt1i500a4" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider w-[80px]" data-id="element-12697" data-mp="18oy1iwicxft4">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-12698" data-mp="32zeb3ueghf7w">
                  {paginatedProducts.map(product => <tr key={product.id} className="hover:bg-[#F8FAFC] transition-colors" data-id="element-12699" data-mp="2y8622zh1fvv9">
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-12700" data-mp="2t8tf8eq62oso">
                        <div className="flex items-center gap-3" data-id="element-12701" data-mp="2t707sx7qc0jm">
                          <div className="w-10 h-10 md:w-12 md:h-12 rounded-md border border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-center flex-shrink-0" data-id="element-12702" data-mp="2obkzekshqwul">
                            <Package className="w-5 h-5 md:w-6 md:h-6 text-#94A3B8" data-id="element-12703" data-mp="3gye2h9tcs87r" />
                          </div>
                          <div className="min-w-0" data-id="element-12704" data-mp="3byt1ktumhpse">
                            <div className="text-[14px] md:text-[16px] font-medium text-[#0F172A] truncate" data-id="element-12705" data-mp="3c1nmsnlgnemv">
                              {product.name}
                            </div>
                            <div className="text-[13px] md:text-[14px] text-[#64748B] truncate" data-id="element-12706" data-mp="20ikviifzfwbz">
                              {product.description}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-12707" data-mp="3p7a1glviwxjy">
                        <span className="text-[14px] md:text-[16px] text-[#0F172A]" data-id="element-12708" data-mp="3ai44z5o2llfr">
                          ${product.rrp.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-12709" data-mp="71581uzj62jv">
                        <span className="text-[14px] md:text-[16px] text-[#0F172A]" data-id="element-12710" data-mp="6ysojde4jk4p">
                          ${product.salePrice.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4 text-right" data-id="element-12711" data-mp="3ta6d1smnbtee">
                        <span className="text-[14px] md:text-[16px] text-[#0F172A]" data-id="element-12712" data-mp="207pvieit8id">
                          {product.stock}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-12713" data-mp="2mt2ulcvog07u">
                        <Badge variant={getStockStatusVariant(product.stockStatus)} data-id="element-12714" data-mp="14u4sotwupeb0">
                          {product.stockStatus}
                        </Badge>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-12715" data-mp="1dv31dpi8i9zx">
                        <div className="flex flex-wrap gap-1" data-id="element-12716" data-mp="1du9yx0qb66nl">
                          {product.tags?.map((tag, index) => <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-[#E0F2FE] text-[#0284C7]" data-id="element-12717" data-mp="19u98ehpxq506">
                              {tag}
                            </span>)}
                        </div>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4 text-right" data-id="element-12718" data-mp="ux0cdaposd24">
                        <span className="text-[13px] md:text-[14px] text-[#64748B]" data-id="element-12719" data-mp="q58k8vqvtz9v">
                          {product.createdAt}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-12720" data-mp="3uk8hiuipuk4d">
                        <div className="flex justify-end" data-id="element-12721" data-mp="3uhvzkhcyk9rb">
                          <div className="relative" data-id="element-12722" data-mp="1xiaytt9ntsbs">
                            <button onClick={() => setOpenProductDropdown(openProductDropdown === product.id ? null : product.id)} className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-12723" data-mp="22i5nrr9vk7u7">
                              <MoreVertical className="w-5 h-5 text-[#64748B]" data-id="element-12724" data-mp="2c7yzqtdxioyz" />
                            </button>

                            {openProductDropdown === product.id && <>
                                <div className="fixed inset-0 z-10" onClick={() => setOpenProductDropdown(null)} data-id="element-12725" data-mp="1emksxuylwbsm" />
                                <div className="absolute right-0 mt-2 w-56 bg-white border border-[#CBD5E1] rounded-md shadow-lg z-20 py-1" data-id="element-12726" data-mp="1oinv30tgkafk">
                                  <button onClick={() => handleViewProductInShopify(product.id)} className="w-full px-4 py-2 text-left text-sm hover:bg-[#F0F9FF] transition-colors text-[#0F172A] flex items-center gap-2" data-id="element-12727" data-mp="1ti8zhqredazo">
                                    <ExternalLink className="w-4 h-4" data-id="element-12728" data-mp="34yqtzlbfp3jo" />
                                    View Product in Shopify
                                  </button>
                                </div>
                              </>}
                          </div>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-12729" data-mp="9mcwexr1vrut">
            <div className="text-[13px] text-[#64748B]" data-id="element-12730" data-mp="5jgkfpw0mtzu">
              Showing {(currentPage_ - 1) * pageSize + 1}-
              {Math.min(currentPage_ * pageSize, filteredProducts.length)} of{' '}
              {filteredProducts.length} products
            </div>
            <div className="flex items-center gap-2" data-id="element-12731" data-mp="3rt9aw5j0l26x">
              <button onClick={() => setCurrentPage_(Math.max(1, currentPage_ - 1))} disabled={currentPage_ === 1} className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 ease-in-out" data-id="element-12732" data-mp="1uxbnqji6dack">
                Previous
              </button>
              <div className="flex items-center gap-1" data-id="element-12733" data-mp="p4xqtlz4o2dp">
                {Array.from({
              length: totalPages
            }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage_(page)} className={`w-10 h-10 rounded-md text-[13px] font-medium transition-colors duration-200 ease-in-out ${page === currentPage_ ? 'bg-[#0EA5E9] text-white' : 'text-[#475569] hover:bg-[#F8FAFC]'}`} data-id="element-12734" data-mp="u1o5c4vaapx0">
                    {page}
                  </button>)}
              </div>
              <button onClick={() => setCurrentPage_(Math.min(totalPages, currentPage_ + 1))} disabled={currentPage_ === totalPages} className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 ease-in-out" data-id="element-12735" data-mp="b4d3cci9pidv">
                Next
              </button>
            </div>
          </div>
        </> : <div className="bg-white rounded-lg border border-[#E2E8F0] p-8 md:p-12 text-center shadow-0_1px_3px_rgba(15,23,42,0.08)" data-id="element-12736" data-mp="3l2demo4be11n">
          <Package className="w-10 h-10 md:w-12 md:h-12 text-#94A3B8 mx-auto mb-4" data-id="element-12737" data-mp="3mezaniuuzi1" />
          <h3 className="text-[18px] md:text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-12738" data-mp="3uyzx56964g0i">
            No products found
          </h3>
          <p className="text-[14px] md:text-[16px] text-[#64748B] mb-6" data-id="element-12739" data-mp="cnmx74hglybl">
            {hasActiveFilters ? 'Try adjusting your filters or search query' : 'Products will appear here once synced from your Shopify store'}
          </p>
          {hasActiveFilters ? <Button variant="outline" onClick={handleClearAll} data-id="element-12740" data-mp="hlwsdf36oyx7">
              Clear all filters
            </Button> : <Button variant="primary" iconLeft={<RefreshCw className="w-4 h-4" data-id="element-12742" data-mp="2odfhx0d07t1u" />} data-id="element-12741" data-mp="2oc4cyx7tof95">
              Sync Products
            </Button>}
        </div>}
    </div>;
}